import { getAuthToken } from '../utils/auth';
import { handleUnauthorized } from '../utils/errorHandler';

export interface TeamPlayer {
  id: string;
  name: string;
  team: string;
  positions: string[];
  primaryPosition: string;
  lineupPosition?: 'QB' | 'RB' | 'WR' | 'TE' | 'FLEX' | 'K' | 'DEF' | 'BN' | 'IR' | string;
  status?: 'active' | 'bench' | 'injured-reserve' | 'not-active';
  injuryStatus?: 'healthy' | 'questionable' | 'doubtful' | 'out' | 'injured-reserve';
  injuryNote?: string;
  imageUrl?: string;
  onByeWeek?: boolean;
  projectedPoints?: number;
  actualPoints?: number;
  seasonPoints?: number;
  averagePoints?: number;
  cannotDrop?: boolean;
  ownershipPercentage?: number;
}

export interface Team {
  id: string;
  name: string;
  managerName?: string;
  rank?: number;
  wins?: number;
  losses?: number;
  ties?: number;
  isOwnedByUser?: boolean;
}

export interface TeamRoster {
  teamId: string;
  teamName: string;
  players: TeamPlayer[];
  starters: TeamPlayer[];
  bench: TeamPlayer[];
  injuredReserve: TeamPlayer[];
}

// Legacy interface for backward compatibility
export interface Roster {
  teamId: string;
  players: Array<{
    id: string;
    name: string;
    team: string;
    positions: string[];
    primaryPosition: string;
  }>;
}

// Fetch all teams in a league
export async function fetchLeagueTeams(leagueId: string): Promise<Team[]> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No auth token found');
  }

  const response = await fetch(
    `${import.meta.env.VITE_BACKEND_BASE_URL}/leagues/${leagueId}/teams`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch teams');
  }

  const data = await response.json();
  return data.teams || [];
}

// Fetch roster for a specific team (legacy format)
export async function fetchTeamRoster(leagueId: string, teamId: string): Promise<Roster> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No auth token found');
  }

  const response = await fetch(
    `${import.meta.env.VITE_BACKEND_BASE_URL}/leagues/${leagueId}/teams/${teamId}/roster`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('UNAUTHORIZED');
    }
    throw new Error('Failed to fetch roster');
  }

  const data = await response.json();
  return data.roster;
}

// Fetch full team roster with detailed player information
export async function fetchTeamRosterDetailed(leagueId: string, teamId: string): Promise<TeamRoster> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No auth token found');
  }

  const response = await fetch(
    `${import.meta.env.VITE_BACKEND_BASE_URL}/leagues/${leagueId}/teams/${teamId}/roster`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('UNAUTHORIZED');
    }
    throw new Error('Failed to fetch roster');
  }

  const data = await response.json();
  return data.roster;
}

// Fetch team details including name and basic info
export async function fetchTeamDetails(leagueId: string, teamId: string): Promise<Team> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No auth token found');
  }

  // First try to get team details from the teams list
  try {
    const teams = await fetchLeagueTeams(leagueId);
    const team = teams.find(t => t.id === teamId);
    if (team) {
      return team;
    }
  } catch (error) {
    console.warn('Could not fetch team from league teams list:', error);
  }

  // Fallback: fetch individual team details if available
  const response = await fetch(
    `${import.meta.env.VITE_BACKEND_BASE_URL}/leagues/${leagueId}/teams/${teamId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('UNAUTHORIZED');
    }
    throw new Error('Failed to fetch team details');
  }

  const data = await response.json();
  return data.team;
}

// Find user's team in a league and fetch their roster
export async function getUserTeamRosterInLeague(leagueId: string): Promise<string[] | null> {
  try {
    // First, fetch all teams in the league
    const teams = await fetchLeagueTeams(leagueId);
    
    // Find the user's team by the isOwnedByUser flag
    const userTeam = teams.find(team => team.isOwnedByUser === true);

    if (!userTeam) {
      console.log('User does not have a team in this league');
      console.log('Teams:', teams);
      return null;
    }
  
    // Fetch the roster for the user's team
    const roster = await fetchTeamRoster(leagueId, userTeam.id);
    
    // Extract just the player names
    return roster.players.map(player => player.name);
  } catch (error) {
    console.error('Error fetching user team roster:', error);
    return null;
  }
}